import { Link } from 'react-router-dom';
import { ArrowRight, CarFront, ArrowLeftRight, Trash2, X } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { useCompare } from '../contexts/CompareContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import CarImage from '../components/CarImage';

const buildComparisonRows = (t, formatPrice) => ([
  {
    label: t('compare.pricePerDay', 'Price / Day'),
    render: (car) => `${formatPrice(car.pricePerDay)} / ${t('common.day', 'day')}`,
  },
  { label: t('compare.category', 'Category'), render: (car) => car.category || 'N/A' },
  { label: t('compare.city', 'City'), render: (car) => car.city || 'N/A' },
  { label: t('compare.year', 'Year'), render: (car) => car.year || 'N/A' },
  { label: t('compare.seats', 'Seats'), render: (car) => car.seats || 'N/A' },
  { label: t('compare.transmission', 'Transmission'), render: (car) => car.transmission || 'N/A' },
  { label: t('compare.fuelType', 'Fuel Type'), render: (car) => car.fuelType || 'N/A' },
  { label: t('compare.engine', 'Engine'), render: (car) => car.engine || 'N/A' },
  { label: t('compare.power', 'Power'), render: (car) => car.power || 'N/A' },
  { label: t('compare.supplier', 'Supplier'), render: (car) => car.supplier || 'N/A' },
]);

export const ComparePage = () => {
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();
  const { compareCars, canOpenComparison, clearCompare, removeFromCompare } = useCompare();
  const rows = buildComparisonRows(t, formatPrice);
  const specColumnAlignment = isRTL ? 'text-right' : 'text-left';
  const endIconMarginClass = isRTL ? 'mr-2' : 'ml-2';
  const startIconMarginClass = isRTL ? 'ml-2' : 'mr-2';

  return (
    <>
      <SEO
        title={t('compare.pageTitle', 'Compare Cars')}
        description={t('compare.pageDescription', 'Compare your selected cars side by side before you contact Velaris Royal.')}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: t('compare.pageTitle', 'Compare Cars') },
        ]}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="compare-page">
        <section className="bg-black py-16 md:py-20">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 sm:px-6 lg:px-8">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
              <ArrowLeftRight className="h-4 w-4" />
              {t('compare.heroEyebrow', 'Side by side')}
            </div>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <h1 className="font-[var(--font-display)] text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                  {t('compare.heading', 'Compare Your Selected Cars')}
                </h1>
                <p className="mt-4 text-base text-white/65 sm:text-lg">
                  {t('compare.subheading', 'Review pricing, specs, and supplier details in one focused view before you make contact.')}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10">
                  <Link to="/cars">
                    {t('compare.browseMore', 'Browse more cars')}
                    <ArrowRight className={`${endIconMarginClass} h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
                  </Link>
                </Button>
                {compareCars.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearCompare}
                    className="border-red-400/20 bg-red-500/10 text-red-200 hover:border-red-400/30 hover:bg-red-500/15"
                  >
                    <Trash2 className={`${startIconMarginClass} h-4 w-4`} />
                    {t('compare.clear', 'Clear comparison')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          {!canOpenComparison ? (
            <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#D4AF37]/10 text-[#D4AF37]">
                <CarFront className="h-8 w-8" />
              </div>
              <h2 className="mt-6 text-2xl font-semibold text-gray-900 dark:text-white">
                {compareCars.length === 0
                  ? t('compare.emptyTitle', 'No cars selected yet')
                  : t('compare.needMoreTitle', 'Add one more car to compare')}
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                {compareCars.length === 0
                  ? t('compare.emptyDescription', 'Use Compare on any car card or detail page to build a side-by-side shortlist.')
                  : t('compare.needMoreDescription', 'Comparison becomes available once you have at least two cars in your tray.')}
              </p>
              <Button asChild className="btn-gold mt-6 text-black">
                <Link to="/cars">{t('btn.browseAllCars', 'Browse All Cars')}</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              <section className="grid gap-4 xl:grid-cols-4" data-testid="compare-selected-cars">
                {compareCars.map((car) => (
                  <article
                    key={car.id}
                    className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition-colors dark:border-gray-800 dark:bg-gray-900"
                  >
                    <div className="relative aspect-[4/3] bg-gray-100 dark:bg-gray-800">
                      <CarImage
                        src={car.image}
                        alt={`${car.brand} ${car.model}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeFromCompare(car.id)}
                        className={`absolute top-3 rounded-full border border-white/20 bg-black/55 p-2 text-white backdrop-blur transition-colors hover:bg-black/80 ${isRTL ? 'left-3' : 'right-3'}`}
                        aria-label={t('compare.remove', 'Remove from comparison')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="space-y-3 p-5">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{car.brand} {car.model}</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{car.year || t('compare.unknownYear', 'Year pending')}</p>
                      </div>
                      <div className="rounded-2xl bg-gray-50 px-4 py-3 dark:bg-gray-800/80">
                        <p className="text-xs uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                          {t('compare.pricePerDay', 'Price / Day')}
                        </p>
                        <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                          {formatPrice(car.pricePerDay)} / {t('common.day', 'day')}
                        </p>
                      </div>
                      <Button asChild variant="outline" className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                        <Link to={`/cars/${car.id}`}>{t('btn.details', 'Details')}</Link>
                      </Button>
                    </div>
                  </article>
                ))}
              </section>

              <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900" data-testid="compare-table">
                <div className="overflow-x-auto">
                  <table dir={isRTL ? 'rtl' : 'ltr'} className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800/80">
                        <th className={`px-5 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 ${specColumnAlignment}`}>
                          {t('compare.specification', 'Specification')}
                        </th>
                        {compareCars.map((car) => (
                          <th
                            key={car.id}
                            className="min-w-[14rem] px-5 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white"
                          >
                            {car.brand} {car.model}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr
                          key={row.label}
                          className={index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/60 dark:bg-gray-900/60'}
                        >
                          <td className={`px-5 py-4 text-sm font-medium text-gray-600 dark:text-gray-300 ${specColumnAlignment}`}>
                            {row.label}
                          </td>
                          {compareCars.map((car) => (
                            <td key={`${row.label}-${car.id}`} className="px-5 py-4 text-center text-sm text-gray-900 dark:text-white">
                              {row.render(car)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ComparePage;
